import Wrapper from '../../Wrapper'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Text, View } from 'react-native'
import { FilterIcon } from '../../../../assets/icons'
import { useFocusEffect } from '@react-navigation/native'
import { useDropzone } from 'react-dropzone'
import { useMessage } from '../../../../utils/helpers/MessageProvider'
import {
  builderInvoiceUpload,
  dropzoneOptions,
  invoiceUploadError,
} from '../../../../utils/helpers/builderInvoiceUpload'
import { InvoicesListDisplay } from '../InvoicesListDisplay'
import { useInvoicesToPay } from '../../../../store/ScreensStore/InvoicesToPayStore'
import Loading from '../../../Loading'
import CreditStore from '../../CreditTab/CreditStore'
import { ReadytoPayWithVirtualCard } from '../../CreditTab/PayNowWithVirtualCard'
import { useTranslation } from 'react-i18next'
import { observer } from 'mobx-react'
import {
  EmptyInvoicePageMessage,
  FabButton,
  Filter,
  GroupedInvoices,
  ISwitchType,
  Switcher,
  SwitchType,
} from './components'
import RootStore from '../../../../store'

export const InvoiceListing = observer(({ navigation, route }) => {
  const { t } = useTranslation('global')
  const [modal, setModal] = useState(false)
  const [invoiceList, setInvoiceList] = useState([])
  const [filterStatus, setFilterStatus] = useState('')
  const [loading, setLoading] = useState(true)
  const [switcher, setSwitcher] = useState<ISwitchType>(SwitchType.INVOICES)
  const { open, acceptedFiles, getInputProps, fileRejections } =
    useDropzone(dropzoneOptions)
  const { hasUnsedActiveCards, fetchLoans } = CreditStore
  const { fetchInvoiceToPay } = useInvoicesToPay()
  const { setMessage } = useMessage()
  const {
    routeParams: { action },
  } = RootStore

  useEffect(() => {
    if (action === 'reloadInvoices') RootStore.initRouteParams({})
  }, [action])

  useEffect(() => {
    fetchLoans()
  }, [fetchLoans])

  useFocusEffect(
    useCallback(() => {
      fetchInvoiceToPay()
        .then((data) => {
          setInvoiceList(data.invoices)
          setFilterStatus('All')
          setLoading(false)
        })
        .catch((e) => {
          setLoading(false)
          console.log(e)
        })
    }, [setInvoiceList, setFilterStatus, setLoading, fetchInvoiceToPay]),
  )

  useEffect(() => {
    if (acceptedFiles.length < 1) {
      return
    }
    builderInvoiceUpload(navigation, acceptedFiles)
  }, [acceptedFiles, navigation])

  useEffect(() => {
    if (fileRejections.length > 0) {
      invoiceUploadError(fileRejections, setMessage)
    }
  }, [fileRejections, setMessage])

  const list = useMemo(() => {
    let invoices
    if (filterStatus === 'All') {
      invoices = invoiceList
    } else {
      invoices = invoiceList.filter((invoice: any) => {
        return invoice.status === filterStatus.split(' ').join('').toUpperCase()
      })
    }

    if (invoices.length === 0 && !loading) {
      return <EmptyInvoicePageMessage />
    }

    if (switcher === SwitchType.VENDORS) {
      return <GroupedInvoices invoices={invoices} t={t} />
    }

    return (
      <InvoicesListDisplay
        invoices={invoices}
        navigation={navigation}
        route={route}
      />
    )
  }, [filterStatus, invoiceList, loading, navigation, route, switcher, t])

  if (loading) return <Loading />

  return (
    <>
      <Wrapper
        toolbar={
          <Toolbar
            onPress={() => setModal(true)}
            t={t}
            switcher={switcher}
            setSwitcher={setSwitcher}
          />
        }
      >
        {hasUnsedActiveCards && (
          <ReadytoPayWithVirtualCard mobileView dontShowExpired />
        )}
        {list}
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <input {...getInputProps()} />
        <Filter
          t={t}
          onPress={(value) => setFilterStatus(value)}
          filterStatus={filterStatus}
          onClose={() => setModal(false)}
          visible={modal}
        />
      </Wrapper>
      <FabButton onPress={open} />
    </>
  )
})

const Toolbar = ({ onPress, t, switcher, setSwitcher }) => (
  <View style={{ padding: 16 }}>
    <View
      style={{
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 16,
      }}
    >
      <Text style={{ fontSize: 18, fontWeight: '600' }}>{t('payables')}</Text>
      <Text
        style={{ fontSize: 15, fontWeight: '500', color: '#335C75' }}
        onPress={onPress}
      >
        <FilterIcon /> {t('InvoiceListing.filters')}
      </Text>
    </View>
    <Switcher active={switcher} onPress={setSwitcher} />
  </View>
)
